import React, { useMemo, useState } from "react";
import type { ApexOptions } from "apexcharts";
import ReactApexChart from "react-apexcharts";

import type { SurveyBreakdownItem, SurveyQuestionSummary } from "../../lib/api";
import {
  CHART_SURFACE,
  MAGNITUDE_COLOR,
  MAX_DONUT_SLICES,
  NPS_COLORS,
  npsGroupColor,
  seriesColor,
  TRACK_COLOR,
} from "./chartPalette";
import {
  formatCount,
  formatPercent,
  isChoiceType,
  isTextType,
  questionTypeLabel,
  respondentName,
} from "./surveyTypes";

type Props = {
  index: number;
  summary: SurveyQuestionSummary;
};

/* -------------------------------------------------------------------------- */
/* Shared pieces                                                               */
/* -------------------------------------------------------------------------- */

const Card: React.FC<{
  index: number;
  summary: SurveyQuestionSummary;
  children: React.ReactNode;
}> = ({ index, summary, children }) => (
  <section className="rounded-2xl border border-[#2C363E] bg-[#161E25]/40 p-4 sm:p-5">
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#5C6B76]">
          Question {index + 1} · {questionTypeLabel(summary.type)}
        </p>
        <h3 className="mt-1 text-[15px] font-semibold leading-snug text-white">
          {summary.prompt}
        </h3>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-[14px] font-semibold tabular-nums text-white">
          {formatCount(summary.answered)}
        </p>
        <p className="text-[11px] text-[#869AA9]">
          answered{summary.skipped > 0 ? ` · ${formatCount(summary.skipped)} skipped` : ""}
        </p>
      </div>
    </div>
    {summary.answered === 0 ? (
      <p className="rounded-xl border border-dashed border-[#2C363E] px-4 py-8 text-center text-[13px] text-[#5C6B76]">
        No answers yet.
      </p>
    ) : (
      children
    )}
  </section>
);

/**
 * The breakdown as text. Doubles as the chart's legend and as the table view, so identity
 * is never carried by colour alone.
 */
const BreakdownList: React.FC<{
  items: SurveyBreakdownItem[];
  colorFor?: (index: number) => string;
}> = ({ items, colorFor }) => (
  <ul className="space-y-2">
    {items.map((item, i) => (
      <li key={item.id} className="flex items-center gap-2.5">
        {colorFor && (
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-sm"
            style={{ backgroundColor: colorFor(i) }}
            aria-hidden
          />
        )}
        <span className="min-w-0 flex-1 truncate text-[13px] text-[#C6D0D7]" title={item.label}>
          {item.label}
        </span>
        <span className="shrink-0 text-[13px] font-semibold tabular-nums text-white">
          {formatPercent(item.percentage)}
        </span>
        <span className="w-14 shrink-0 text-right text-[11px] tabular-nums text-[#5C6B76]">
          {formatCount(item.count)}
        </span>
      </li>
    ))}
  </ul>
);

/** Horizontal magnitude bars — one hue, value direct-labelled, recessive track. */
const BarList: React.FC<{
  items: SurveyBreakdownItem[];
  color?: string;
}> = ({ items, color = MAGNITUDE_COLOR }) => {
  // Scale to the largest bar, not to 100 — multi-choice rarely gets near 100%.
  const max = Math.max(...items.map((i) => i.percentage), 1);

  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item.id}>
          <div className="mb-1.5 flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-[13px] text-[#C6D0D7]" title={item.label}>
              {item.label}
            </span>
            <span className="shrink-0 text-[12px] tabular-nums text-[#869AA9]">
              <span className="font-semibold text-white">
                {formatPercent(item.percentage)}
              </span>{" "}
              · {formatCount(item.count)}
            </span>
          </div>
          <span
            className="block h-2 w-full overflow-hidden rounded-full"
            style={{ backgroundColor: TRACK_COLOR }}
            aria-hidden
          >
            <span
              className="block h-full rounded-full transition-[width] duration-500"
              style={{
                width: `${(item.percentage / max) * 100}%`,
                backgroundColor: color,
              }}
            />
          </span>
        </li>
      ))}
    </ul>
  );
};

/** Free-text answers, capped with a show-all toggle so one card cannot run the page. */
const TextFeed: React.FC<{
  samples: NonNullable<SurveyQuestionSummary["textSamples"]>;
  title: string;
}> = ({ samples, title }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? samples : samples.slice(0, 5);

  return (
    <div>
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#5C6B76]">
        {title} ({formatCount(samples.length)})
      </p>
      <ul className={`space-y-2 ${expanded ? "max-h-[420px] overflow-y-auto pr-1" : ""}`}>
        {visible.map((s) => (
          <li
            key={s.responseId}
            className="rounded-xl border border-[#2C363E] bg-[#13181B] px-3.5 py-3"
          >
            <p className="text-[13px] leading-relaxed text-[#E7EBEE]">{s.text}</p>
            <p className="mt-1.5 text-[11px] text-[#5C6B76]">
              {respondentName(s.user)}
            </p>
          </li>
        ))}
      </ul>
      {samples.length > 5 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2.5 text-[12px] font-semibold text-secondary_button hover:brightness-110"
        >
          {expanded ? "Show fewer" : `Show all ${formatCount(samples.length)}`}
        </button>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Per-type bodies                                                             */
/* -------------------------------------------------------------------------- */

const ChoiceBody: React.FC<{ summary: SurveyQuestionSummary }> = ({ summary }) => {
  const breakdown = useMemo(
    () => (summary.breakdown ?? []).filter((b) => b.count > 0 || b.percentage > 0),
    [summary.breakdown]
  );
  const allItems = summary.breakdown ?? [];

  // Single-choice is mutually exclusive and sums to 100, so a donut is honest. Multi-choice
  // does not sum to 100 — a donut there would be a lie, so it always gets bars. Past eight
  // slices a donut is unreadable regardless, so that falls back to bars too.
  const useDonut =
    summary.type === "SINGLE_CHOICE" && allItems.length <= MAX_DONUT_SLICES;

  const chart = useMemo(() => {
    const series = breakdown.map((b) => b.count);
    const options: ApexOptions = {
      chart: {
        type: "donut",
        background: "transparent",
        animations: { enabled: true },
      },
      labels: breakdown.map((b) => b.label),
      colors: breakdown.map((_, i) => seriesColor(i)),
      // A 2px surface-coloured gap so adjacent slices read as separate marks.
      stroke: { width: 2, colors: [CHART_SURFACE] },
      legend: { show: false },
      dataLabels: { enabled: false },
      tooltip: {
        theme: "dark",
        y: {
          formatter: (v: number) =>
            `${formatCount(v)} (${formatPercent(
              summary.answered === 0 ? 0 : (v / summary.answered) * 100
            )})`,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "68%",
            labels: {
              show: true,
              name: { show: false },
              value: {
                show: true,
                color: "#FFFFFF",
                fontSize: "22px",
                fontFamily: "Sora, sans-serif",
                fontWeight: 700,
                formatter: () => formatCount(summary.answered),
              },
              total: {
                show: true,
                showAlways: true,
                label: "answers",
                color: "#869AA9",
                fontSize: "11px",
                formatter: () => formatCount(summary.answered),
              },
            },
          },
        },
      },
    };
    return { series, options };
  }, [breakdown, summary.answered]);

  return (
    <div>
      {useDonut ? (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,240px)_minmax(0,1fr)] lg:items-center">
          <div className="mx-auto w-full max-w-[240px]">
            <ReactApexChart
              options={chart.options}
              series={chart.series}
              type="donut"
              height={240}
            />
          </div>
          <BreakdownList items={allItems} colorFor={seriesColor} />
        </div>
      ) : (
        <BarList items={allItems} />
      )}

      {summary.otherTexts && summary.otherTexts.length > 0 && (
        <div className="mt-5 border-t border-[#2C363E] pt-4">
          <TextFeed samples={summary.otherTexts} title="“Other” answers" />
        </div>
      )}
    </div>
  );
};

const TextBody: React.FC<{ summary: SurveyQuestionSummary }> = ({ summary }) => {
  const usage = (summary.suggestionUsage ?? []).filter((u) => u.count > 0);

  return (
    <div className="space-y-5">
      {usage.length > 0 && (
        <div>
          <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-[#5C6B76]">
            Suggested answers used
          </p>
          <BarList items={usage} />
        </div>
      )}
      {summary.textSamples && summary.textSamples.length > 0 && (
        <TextFeed samples={summary.textSamples} title="Responses" />
      )}
    </div>
  );
};

const NpsBody: React.FC<{ summary: SurveyQuestionSummary }> = ({ summary }) => {
  const nps = summary.nps;
  if (!nps) return null;

  const max = Math.max(...nps.distribution, 1);
  const groups = [
    { label: "Detractors", hint: "0–6", value: nps.detractors, color: NPS_COLORS.detractor },
    { label: "Passives", hint: "7–8", value: nps.passives, color: NPS_COLORS.passive },
    { label: "Promoters", hint: "9–10", value: nps.promoters, color: NPS_COLORS.promoter },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
        <div>
          <p className="font-sora text-[40px] font-bold leading-none tabular-nums text-white">
            {nps.score > 0 ? `+${nps.score}` : nps.score}
          </p>
          <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#5C6B76]">
            NPS score
          </p>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-3">
          {groups.map((g) => (
            <div key={g.label} className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: g.color }}
                aria-hidden
              />
              <div>
                <p className="text-[13px] font-semibold tabular-nums text-white">
                  {formatCount(g.value)}{" "}
                  <span className="font-normal text-[#869AA9]">
                    ({formatPercent(
                      summary.answered === 0 ? 0 : (g.value / summary.answered) * 100
                    )})
                  </span>
                </p>
                <p className="text-[11px] text-[#869AA9]">
                  {g.label} · {g.hint}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-end gap-1.5" style={{ height: 96 }}>
          {nps.distribution.map((count, score) => (
            <div
              key={score}
              className="group flex min-w-0 flex-1 flex-col items-center justify-end"
              title={`${count} rated ${score}`}
            >
              <span className="mb-1 text-[10px] tabular-nums text-[#5C6B76] opacity-0 transition group-hover:opacity-100">
                {count}
              </span>
              <span
                className="block w-full rounded-t"
                style={{
                  height: `${Math.max(2, (count / max) * 72)}px`,
                  backgroundColor: npsGroupColor(score),
                }}
                aria-hidden
              />
            </div>
          ))}
        </div>
        <div className="mt-1.5 flex gap-1.5">
          {nps.distribution.map((_, score) => (
            <span
              key={score}
              className="min-w-0 flex-1 text-center text-[10px] tabular-nums text-[#5C6B76]"
            >
              {score}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const RatingBody: React.FC<{ summary: SurveyQuestionSummary }> = ({ summary }) => {
  const rating = summary.rating;
  if (!rating) return null;

  const items: SurveyBreakdownItem[] = rating.distribution.map((count, i) => ({
    id: String(i + 1),
    label: `${i + 1} star${i === 0 ? "" : "s"}`,
    count,
    percentage:
      summary.answered === 0 ? 0 : (count / summary.answered) * 100,
  }));

  return (
    <div className="space-y-5">
      <div>
        <p className="font-sora text-[40px] font-bold leading-none tabular-nums text-white">
          {rating.average.toFixed(1)}
        </p>
        <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#5C6B76]">
          Average rating
        </p>
      </div>
      <BarList items={items.slice().reverse()} />
    </div>
  );
};

/* -------------------------------------------------------------------------- */

const QuestionResultCard: React.FC<Props> = ({ index, summary }) => (
  <Card index={index} summary={summary}>
    {isChoiceType(summary.type) && <ChoiceBody summary={summary} />}
    {isTextType(summary.type) && <TextBody summary={summary} />}
    {summary.type === "NPS" && <NpsBody summary={summary} />}
    {summary.type === "RATING" && <RatingBody summary={summary} />}
  </Card>
);

export default QuestionResultCard;
